import { Injector, AbstractType, OnDestroy, Directive } from '@angular/core';
import { ChangeLogEvent } from '@app/core/db/change-log';
import { ChangeLogService, Subscription } from '@app/core/services/change-log.service';
import { OfflineDataDb, WorkOrderDataItem } from '@app/core/db/offline-data';
import { BadgeNotification } from '@app/core/model/badge-notification.model';
import { ApiLoadErrorService } from '@app/core/services/api-load-error.service';
import { LoaderService } from '@app/header/loader/loader.service';
import { WorkOrderDownloadOptions } from '@app/core/model/document-download';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Directive()
export abstract class AbstractWorkOrderDataService<T> implements OnDestroy {
  protected workOrderId: string | null = null;

  private initialized = false;

  private data: T | null = null;
  public data$ = new BehaviorSubject<T | null>(null);

  private parentData: any = null;

  private subscription: Subscription | null = null;

  private dataServices: AbstractWorkOrderDataService<any>[] = [];
  protected offlineData: OfflineDataDb;
  protected changeLogService: ChangeLogService;

  private readonly destroy$ = new Subject<void>();

  protected constructor(
    private injector: Injector,
    private dataType: string,
    private dataServiceTypes: AbstractType<AbstractWorkOrderDataService<any>>[] = [],
  ) {
    this.changeLogService = this.injector.get(ChangeLogService);
    this.offlineData = this.injector.get(OfflineDataDb);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected abstract load(): void;

  initialize(workOrderId: string, parentData: any = null): void {
    this.initialized = false;
    this.workOrderId = workOrderId;
    this.parentData = parentData;

    if (navigator.onLine) {
      this.load();
    } else {
      this.rehydrate();
    }
  }

  protected initChildren(workOrderId: string, parentData: any) {
    const dataServices = this.dataServiceTypes.map((t) => this.injector.get(t));
    dataServices.forEach((ds) => {
      ds.initialize(workOrderId, parentData);
    });
    this.dataServices = dataServices;
  }

  async enableChangeDetection(): Promise<void> {
    if (!this.subscription && this.workOrderId) {
      this.subscription = this.changeLogService.subscribe(this.workOrderId, (e) => this.apply(this.data, e));
      await this.subscription.replay();
      await Promise.all(this.dataServices.map((ds) => ds.enableChangeDetection()));
    }
  }

  dispose() {
    if (this.subscription) {
      this.subscription.dispose();
      this.subscription = null;
    }

    this.workOrderId = null;
    this.setData(null);
    this.initialized = false;

    this.dataServices.forEach((ds) => ds.dispose());
    this.dataServices = [];
  }

  get(): T | null {
    return this.data;
  }

  getData(): Observable<T | null> {
    return this.data$.pipe(distinctUntilChanged());
  }

  setData(data: T | null) {
    this.data = data;
    this.data$.next(data);
    this.initialized = true;
    if (navigator.onLine) {
      void this.refreshData();
    }
    this.enableChangeDetection();
  }

  getParent(): any {
    return this.parentData;
  }

  async download(options?: WorkOrderDownloadOptions) {
    if (!this.initialized || this.data == null) {
      return;
    }

    const data = {
      workOrderId: this.workOrderId,
      type: this.dataType,
      data: this.data,
    } as WorkOrderDataItem;

    await this.offlineData.workOrderDataItems.put(data);
    await Promise.all(this.dataServices.map((ds) => ds.download(options)));
  }

  getDataService(dataType: string): AbstractWorkOrderDataService<any> | null {
    for (const ds of this.dataServices) {
      if (ds.dataType === dataType) {
        return ds;
      }
    }
    return null;
  }

  protected apply(_data: T | null, _event: ChangeLogEvent) {}

  /**
   * Decides how to react when the online load of this resource fails. The app only falls back to
   * downloaded (IndexedDB) data when it is genuinely offline, confirmed by the same check-update
   * probe that drives the offline banner. On a reachable server error (e.g. HTTP 500) we do not
   * present stale offline data; instead the caller-supplied empty state is shown, matching the
   * behaviour used when nothing was downloaded.
   */
  protected async handleLoadFailure(emptyValue: T, error?: unknown): Promise<void> {
    if (navigator.onLine) {
      const loaderService = this.injector.get(LoaderService);
      const reachable = await loaderService.confirmBackendReachable();
      if (reachable) {
        const apiLoadErrorService = this.injector.get(ApiLoadErrorService);
        apiLoadErrorService.report(
          this.dataType,
          `Failed to load ${this.dataType.toLowerCase()}. Showing a limited view instead.`,
        );
        this.setData(emptyValue);
        return;
      }
    }
    this.rehydrate();
  }

  protected rehydrate(): void {
    this.offlineData.workOrderDataItems
      .where({ workOrderId: this.workOrderId, type: this.dataType })
      .first()
      .then((item) => {
        if (!item) {
          console.warn(`[${this.dataType}] No offline data found for workOrderId=${this.workOrderId}`);
          return;
        }
        this.setData(item.data);
      })
      .catch((error) => {
        console.warn(`[${this.dataType}] rehydrate error for workOrderId=${this.workOrderId}`, error);
      });
  }

  private async refreshData() {
    if (!this.initialized || this.data == null || !this.workOrderId) {
      return;
    }

    const isOfflineEnabled = await this.offlineData.workOrders.where({ workOrderId: this.workOrderId }).count();

    if (!isOfflineEnabled) {
      return;
    }

    await this.offlineData.workOrderDataItems
      .where({ workOrderId: this.workOrderId, type: this.dataType })
      .modify((wo) => {
        wo.data = this.data;
      })
      .catch((e) => console.error(e));
  }

  private resolveDataServiceName(notification: BadgeNotification) {
    switch (notification.name) {
      case 'CommentEntity':
        return 'COMMENTS';
      case 'DocumentEntity':
        return 'DOCUMENTS';
      default:
        return 'WORK_ORDER';
    }
  }

  public refreshOfflineData(notification: BadgeNotification) {
    for (const n of notification.children) {
      const dataService = this.getDataService(this.resolveDataServiceName(n));
      if (dataService) {
        dataService.download();
      }
    }
  }
}
